import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hexToHsl } from "@/hooks/useBranding";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { PublicPage, PublicHeader, LogoMark } from "@/components/public/PublicShell";
import { RoleCard, RoleView } from "@/components/public/Roles";
import { MapPin, ArrowLeft, Briefcase, Globe } from "lucide-react";

// The org's brand colour drives every primary accent on its careers page; text on it stays readable.
function brandVars(hex) {
  const hsl = hexToHsl(hex);
  if (!hsl) return undefined;
  const light = parseFloat(hsl.split(" ")[2]) >= 55;
  return { "--primary": hsl, "--ring": hsl, "--primary-foreground": light ? "156 65% 5%" : "0 0% 100%" };
}

export default function OrgCareers() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    api.get(`/public/careers/${slug}`).then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "This careers page isn't available." : e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [slug]);

  const openJob = (id) => api.get(`/public/jobs/${id}`).then((r) => { setSelected(r.data); window.scrollTo({ top: 0, behavior: "smooth" }); }).catch(() => {});
  const apply = (id) => { if (user?.role === "candidate") nav(`/candidate/apply/${id}`); else nav("/login"); };

  if (loading) return <PublicPage className="flex items-center justify-center"><LoadingState label="Loading careers…" /></PublicPage>;
  if (err) return <PublicPage className="flex items-center justify-center p-8"><ErrorState message={err} onRetry={load} /></PublicPage>;

  const b = data.org;
  const jobs = data.jobs || [];

  return (
    <PublicPage data-testid="org-careers-page" style={brandVars(b.primary || "#10B981")}>
      <title>{`${b.org_name} — Careers`}</title>
      <meta property="og:title" content={`${b.org_name} — Careers`} />
      <meta property="og:description" content={b.about || "Explore open roles — AI-assisted, human-reviewed hiring."} />
      <meta property="og:image" content={`${window.location.origin}/api/public/careers/${data.slug}/og.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <PublicHeader right={<Button size="sm" className="rounded-full px-4 font-semibold" onClick={() => nav(user ? "/" : "/login")} data-testid="org-careers-signin">{user ? "Dashboard" : "Sign in"}</Button>}>
        <button onClick={() => setSelected(null)} className="flex items-center gap-2.5 min-w-0" data-testid="org-careers-brand">
          <LogoMark logo={b.logo} />
          <span className="font-display font-bold text-lg truncate">{b.org_name}</span>
          <span className="hidden sm:inline text-muted-foreground">Careers</span>
        </button>
      </PublicHeader>

      {selected ? (
        <main className="mx-auto max-w-[1200px] px-4 sm:px-8 py-10" data-testid="org-careers-detail">
          <button onClick={() => setSelected(null)} className="mb-8 text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="org-careers-back"><ArrowLeft className="h-4 w-4" />All roles</button>
          <RoleView job={selected} company={b.org_name} user={user} onApply={() => apply(selected.id)} applyTestId="org-careers-apply" />
        </main>
      ) : (
        <>
          <section className="relative overflow-hidden border-b border-border/70">
            <div className="pointer-events-none absolute -top-32 right-0 h-80 w-80 rounded-full bg-primary/15 blur-[120px]" aria-hidden />
            <div className="relative mx-auto max-w-[1360px] px-4 sm:px-8 py-14 sm:py-20 short:py-12 space-y-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/12 px-3 py-1 text-xs font-medium text-primary"><Briefcase className="h-3.5 w-3.5" /> Open roles at {b.org_name}</span>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Build the future with {b.org_name}.</h1>
              <p className="text-muted-foreground max-w-xl text-base leading-relaxed">{b.about || "Every assessment is explainable and reviewed by a person, with a fair, non-biometric path always available."}</p>
              <div className="flex flex-wrap gap-4 pt-1 text-sm text-muted-foreground">
                {b.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" /> {b.location}</span>}
                {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:underline"><Globe className="h-4 w-4 text-primary" /> Company site</a>}
                <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-primary" /> {jobs.length} open role{jobs.length === 1 ? "" : "s"}</span>
              </div>
            </div>
          </section>
          <main className="mx-auto max-w-[1360px] px-4 sm:px-8 py-8">
            {jobs.length === 0
              ? <EmptyState icon={Briefcase} title="No open roles right now" description="Check back soon. New roles are posted regularly." />
              : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="org-careers-grid">{jobs.map((j) => <RoleCard key={j.id} job={j} company={b.org_name} onOpen={() => openJob(j.id)} testId={`org-careers-role-${j.id}`} />)}</div>}
          </main>
        </>
      )}
      <footer className="border-t border-border/70 py-6 text-center text-xs text-muted-foreground">
        Powered by <span className="font-semibold">HireStream</span> · AI-assisted, human-reviewed hiring
      </footer>
    </PublicPage>
  );
}
